import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Section } from '../ui/Section';
import { GlassCard } from '../ui/GlassCard';
import { Container } from '../ui/Container';
import { productivityApps } from '../../data/apps';

const SuiteHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto var(--space-12);
`;

const Eyebrow = styled.span`
  display: inline-block;
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  color: var(--color-brand-primary);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: var(--space-4);
`;

const Title = styled.h2`
  font-size: var(--text-4xl);
  margin-bottom: var(--space-4);

  @media (max-width: 768px) {
    font-size: var(--text-3xl);
  }
`;

const Subtitle = styled.p`
  font-size: var(--text-xl);
  color: var(--color-text-secondary);
  line-height: var(--leading-relaxed);
  margin-bottom: var(--space-6);

  @media (max-width: 768px) {
    font-size: var(--text-lg);
  }
`;

const VisionCard = styled(GlassCard)`
  max-width: 700px;
  margin: 0 auto var(--space-12);
  text-align: center;
  background: linear-gradient(
    135deg,
    rgba(81, 20, 97, 0.08) 0%,
    rgba(0, 122, 255, 0.08) 100%
  );
`;

const VisionText = styled.p`
  font-size: var(--text-lg);
  line-height: var(--leading-relaxed);
  color: var(--color-text-primary);
`;

const AppsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-6);
  margin-bottom: var(--space-12);

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const AppCard = styled(Link)<{ $color: string; $comingSoon?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--space-6);
  background: var(--glass-background);
  backdrop-filter: var(--glass-blur);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-xl);
  text-decoration: none;
  transition: all var(--transition-base);
  position: relative;
  opacity: ${props => props.$comingSoon ? 0.7 : 1};

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-lg);
    border-color: ${props => props.$color};
  }
`;

const AppLogo = styled.div<{ $color: string }>`
  width: 72px;
  height: 72px;
  border-radius: var(--radius-xl);
  background: ${props => props.$color}15;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--space-4);
  overflow: hidden;
`;

const AppLogoImg = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;
`;

const AppName = styled.h3`
  font-size: var(--text-2xl);
  color: var(--color-text-primary);
  margin-bottom: var(--space-1);
`;

const AppQuestion = styled.div<{ $color: string }>`
  font-size: var(--text-sm);
  color: ${props => props.$color};
  font-weight: var(--font-medium);
  margin-bottom: var(--space-2);
`;

const AppTagline = styled.p`
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
`;

const StatusBadge = styled.span<{ $status: string }>`
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  font-size: var(--text-xs);
  font-weight: var(--font-medium);
  background: ${props => {
    switch (props.$status) {
      case 'live': return 'rgba(52, 199, 89, 0.15)';
      case 'beta': return 'rgba(255, 149, 0, 0.15)';
      case 'coming-soon': return 'rgba(142, 142, 147, 0.15)';
      default: return 'rgba(142, 142, 147, 0.15)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'live': return 'var(--color-success)';
      case 'beta': return '#ff9500';
      case 'coming-soon': return 'var(--color-text-tertiary)';
      default: return 'var(--color-text-tertiary)';
    }
  }};
`;

const ConnectionDiagram = styled.div`
  max-width: 600px;
  margin: 0 auto var(--space-12);
  padding: var(--space-8);
  background: var(--glass-background);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-xl);
`;

const DiagramTitle = styled.h4`
  text-align: center;
  font-size: var(--text-lg);
  margin-bottom: var(--space-6);
  color: var(--color-text-primary);
`;

const DataFlow = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

const FlowStep = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-4);
`;

const FlowIcon = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: var(--font-bold);
  flex-shrink: 0;
`;

const FlowText = styled.div`
  flex: 1;
`;

const FlowTitle = styled.div`
  font-weight: var(--font-semibold);
  margin-bottom: var(--space-1);
`;

const FlowDescription = styled.div`
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

const Arrow = styled.div`
  text-align: center;
  color: var(--color-text-tertiary);
  font-size: var(--text-xl);
  padding: var(--space-2) 0;
`;

const AISection = styled.div`
  text-align: center;
  padding: var(--space-8);
  background: linear-gradient(
    135deg,
    rgba(0, 212, 170, 0.1) 0%,
    rgba(81, 20, 97, 0.1) 100%
  );
  border-radius: var(--radius-xl);
  border: 1px solid var(--glass-border);
`;

const AITitle = styled.h4`
  font-size: var(--text-xl);
  margin-bottom: var(--space-3);
`;

const AIDescription = styled.p`
  color: var(--color-text-secondary);
  max-width: 500px;
  margin: 0 auto;
`;

const appQuestions: Record<string, string> = {
  'what': 'What do I have?',
  'when': 'When should I do it?',
  'who': 'Who is involved?',
  'how': 'How do I do it?',
};

const statusLabels: Record<string, string> = {
  'live': 'Live',
  'beta': 'Beta',
  'coming-soon': 'Coming Soon',
};

export function ProductivitySuite() {
  return (
    <Section background="secondary">
      <Container>
        <SuiteHeader>
          <Eyebrow>The W-Series</Eyebrow>
          <Title>Your Life, Structured for AI</Title>
          <Subtitle>
            Four interconnected apps that answer life's essential questions.
            Together, they create a personal knowledge graph that makes AI genuinely useful.
          </Subtitle>
        </SuiteHeader>

        <VisionCard>
          <VisionText>
            AI assistants are only as helpful as the context you give them. The W-Series captures
            the essential data about your life - your belongings, your schedule, your relationships,
            and your knowledge - in a structured format that AI can actually understand and use.
          </VisionText>
        </VisionCard>

        <AppsGrid>
          {productivityApps.map(app => (
            <AppCard
              key={app.id}
              to={`/products/${app.id}`}
              $color={app.accentColor}
              $comingSoon={app.status === 'coming-soon'}
            >
              <StatusBadge $status={app.status}>
                {statusLabels[app.status]}
              </StatusBadge>
              <AppLogo $color={app.accentColor}>
                <AppLogoImg src={app.logo} alt={`${app.name} logo`} />
              </AppLogo>
              <AppName>{app.name}</AppName>
              <AppQuestion $color={app.accentColor}>
                {appQuestions[app.id]}
              </AppQuestion>
              <AppTagline>{app.tagline}</AppTagline>
            </AppCard>
          ))}
        </AppsGrid>

        <ConnectionDiagram>
          <DiagramTitle>How It Works Together</DiagramTitle>
          <DataFlow>
            <FlowStep>
              <FlowIcon $color="#007aff">W</FlowIcon>
              <FlowText>
                <FlowTitle>What tracks your inventory</FlowTitle>
                <FlowDescription>Know what you own, where it is, and what it's worth</FlowDescription>
              </FlowText>
            </FlowStep>
            <Arrow>+</Arrow>
            <FlowStep>
              <FlowIcon $color="#511461">W</FlowIcon>
              <FlowText>
                <FlowTitle>When manages your time</FlowTitle>
                <FlowDescription>Coordinate schedules, events, and availability</FlowDescription>
              </FlowText>
            </FlowStep>
            <Arrow>+</Arrow>
            <FlowStep>
              <FlowIcon $color="#ff9500">W</FlowIcon>
              <FlowText>
                <FlowTitle>Who maps your relationships</FlowTitle>
                <FlowDescription>Remember details about people who matter</FlowDescription>
              </FlowText>
            </FlowStep>
            <Arrow>+</Arrow>
            <FlowStep>
              <FlowIcon $color="#af52de">H</FlowIcon>
              <FlowText>
                <FlowTitle>How stores your knowledge</FlowTitle>
                <FlowDescription>Capture procedures and how-tos you've learned</FlowDescription>
              </FlowText>
            </FlowStep>
            <Arrow>=</Arrow>
            <AISection>
              <AITitle>AI That Actually Knows You</AITitle>
              <AIDescription>
                With structured data about your life, AI can give you genuinely personalized
                help - not generic answers, but advice based on your actual situation.
              </AIDescription>
            </AISection>
          </DataFlow>
        </ConnectionDiagram>
      </Container>
    </Section>
  );
}
